import React, { useEffect, useRef } from 'react';

import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

/**
 * Read-only route preview map — MapLibre GL with free CARTO raster tiles (no
 * API key, no Google). Plots located stops as numbered pins in sequence, draws
 * a connector line, and fits the view to them. Stops without coordinates are
 * simply not plotted (the caller flags "N of M located" alongside).
 */
export interface RouteStopPoint {
  id: string;
  sequence: number;
  /** PICKUP pins use the accent; DROP_OFF use a warm tone — matches the list. */
  type: 'PICKUP' | 'DROP_OFF';
  latitude?: number;
  longitude?: number;
}

// Free, key-less basemap. CARTO Positron raster tiles (OSM data) — attribution
// required, which MapLibre renders from the source definition below.
const CARTO_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    carto: {
      type: 'raster',
      tiles: [
        'https://a.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
        'https://b.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
        'https://c.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> © <a href="https://carto.com/attributions">CARTO</a>',
    },
  },
  layers: [{ id: 'carto', type: 'raster', source: 'carto' }],
};

const PIN_COLOR: Record<RouteStopPoint['type'], string> = {
  PICKUP: '#5E2AAC', // accent purple
  DROP_OFF: '#1FC16B', // success green
};

/**
 * Build a *schematic* connector — a gentle arc between each consecutive stop so
 * the line reads clearly as "stop order," never mistaken for a real road route.
 * The actual road-following polyline is computed by the backend after the route
 * is created; this is only a pre-submit sequence preview.
 */
const buildSchematicPath = (
  pts: [number, number][]
): [number, number][] => {
  if (pts.length < 2) return pts;
  const SAMPLES = 24; // points sampled per leg
  const BOW = 0.15; // arc height as a fraction of the leg length
  const out: [number, number][] = [pts[0]];
  for (let i = 0; i < pts.length - 1; i++) {
    const [x0, y0] = pts[i];
    const [x1, y1] = pts[i + 1];
    const dx = x1 - x0;
    const dy = y1 - y0;
    const len = Math.hypot(dx, dy) || 1;
    // Control point: leg midpoint pushed along the (consistent) perpendicular.
    const cx = (x0 + x1) / 2 + (-dy / len) * len * BOW;
    const cy = (y0 + y1) / 2 + (dx / len) * len * BOW;
    for (let s = 1; s <= SAMPLES; s++) {
      const t = s / SAMPLES;
      const mt = 1 - t;
      // Quadratic bezier.
      out.push([
        mt * mt * x0 + 2 * mt * t * cx + t * t * x1,
        mt * mt * y0 + 2 * mt * t * cy + t * t * y1,
      ]);
    }
  }
  return out;
};

/**
 * Fetch the real road-following geometry through the stops (in order) from the
 * free, key-less public OSRM server. Returns the decoded LineString coords, or
 * null on any failure — the caller keeps the schematic connector as fallback.
 * This is only a preview estimate; the backend computes the authoritative
 * polyline (Mapbox snapshot) after the shipment is created.
 */
const fetchRoadRoute = async (
  coords: [number, number][],
  _seq: number,
  signal: AbortSignal
): Promise<[number, number][] | null> => {
  if (coords.length < 2) return null;
  // OSRM caps coordinates per request; 25 is well within our stop counts.
  const path = coords
    .slice(0, 25)
    .map(([lng, lat]) => `${lng},${lat}`)
    .join(';');
  const url = `https://router.project-osrm.org/route/v1/driving/${path}?overview=full&geometries=geojson`;
  try {
    const res = await fetch(url, { signal });
    if (!res.ok) return null;
    const data = await res.json();
    const geom = data?.routes?.[0]?.geometry?.coordinates;
    if (Array.isArray(geom) && geom.length >= 2) {
      return geom as [number, number][];
    }
    return null;
  } catch {
    return null; // network error / aborted / CORS — fall back to schematic
  }
};

const makePin = (seq: number, color: string): HTMLElement => {
  const el = document.createElement('div');
  el.textContent = String(seq);
  el.style.cssText = [
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'width:26px',
    'height:26px',
    'border-radius:9999px',
    `background:${color}`,
    'color:#fff',
    'font:600 12px/1 "Plus Jakarta Sans",system-ui,sans-serif',
    'border:2px solid #fff',
    'box-shadow:0 1px 4px rgba(0,0,0,0.3)',
  ].join(';');
  return el;
};

const RouteStopsMap: React.FC<{ stops: RouteStopPoint[]; className?: string }> = ({
  stops,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);
  // Monotonic counter so a slow OSRM response can't overwrite a newer one.
  const requestSeqRef = useRef(0);

  // Create the map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: CARTO_STYLE,
      center: [107.9, -6.24], // Patimban-ish default until fitBounds runs
      zoom: 8,
      attributionControl: { compact: true },
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;
    return () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Re-plot markers + connector line + fit whenever the located stops change.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const located = stops
      .filter((s) => s.latitude != null && s.longitude != null)
      .sort((a, b) => a.sequence - b.sequence);

    // Draw (or update) the route line from a coordinate list.
    const setLine = (coordinates: [number, number][]) => {
      const line: GeoJSON.Feature<GeoJSON.LineString> = {
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates },
      };
      const src = map.getSource('route-line') as maplibregl.GeoJSONSource | undefined;
      if (src) {
        src.setData(line);
      } else {
        map.addSource('route-line', { type: 'geojson', data: line });
        map.addLayer({
          id: 'route-line',
          type: 'line',
          source: 'route-line',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': '#5E2AAC',
            'line-width': 3,
            'line-opacity': 0.85,
          },
        });
      }
    };

    const apply = () => {
      // Markers
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = located.map((s) =>
        new maplibregl.Marker({ element: makePin(s.sequence, PIN_COLOR[s.type]) })
          .setLngLat([s.longitude as number, s.latitude as number])
          .addTo(map)
      );

      const stopCoords = located.map(
        (s) => [s.longitude as number, s.latitude as number] as [number, number]
      );

      // Draw the schematic connector immediately so the map is never empty,
      // then upgrade in place to the real road-following geometry once OSRM
      // responds (falls back to the schematic line if the request fails).
      if (stopCoords.length >= 2) {
        setLine(buildSchematicPath(stopCoords));
        fetchRoadRoute(stopCoords, requestSeqRef.current, controller.signal).then(
          (road) => {
            if (road && requestSeqRef.current === mySeq && mapRef.current) {
              setLine(road);
            }
          }
        );
      }

      // Fit
      if (located.length === 1) {
        map.easeTo({
          center: [located[0].longitude as number, located[0].latitude as number],
          zoom: 13,
          duration: 300,
        });
      } else if (located.length > 1) {
        const bounds = new maplibregl.LngLatBounds();
        located.forEach((s) =>
          bounds.extend([s.longitude as number, s.latitude as number])
        );
        map.fitBounds(bounds, { padding: 48, maxZoom: 14, duration: 300 });
      }
      map.resize();
    };

    // Guard against out-of-order async responses (stops changed mid-fetch).
    const controller = new AbortController();
    const mySeq = ++requestSeqRef.current;

    if (map.isStyleLoaded()) apply();
    else map.once('load', apply);

    return () => {
      controller.abort();
    };
  }, [stops]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ width: '100%', height: 240, borderRadius: 12, overflow: 'hidden' }}
    />
  );
};

export default RouteStopsMap;
